import { ErrorMessage, Input } from "apps/portal/src/components/atoms";
import { Link04Icon } from "apps/portal/src/components/atoms/icons/Link04Icon";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { roundToTwoDecimals } from "./utils/roundToTwoDecimals";

export type MembersPriceValueType = {
  exTaxPrice: number;
  incTaxPrice: number;
};

type MembersPriceBlockProps = {
  value: MembersPriceValueType;
  onChange: (value: MembersPriceValueType) => void;
  taxRate: number;
  exTaxError?: string;
  incTaxError?: string;
  customLabel?: string;
};

export const MembersPriceBlock = ({
  value,
  onChange,
  taxRate,
  exTaxError,
  incTaxError,
  customLabel,
}: MembersPriceBlockProps) => {
  const { t } = useTranslation();

  const { exTaxPrice, incTaxPrice } = value;

  const prevTaxRateRef = useRef(taxRate);

  useEffect(() => {
    if (prevTaxRateRef.current !== taxRate) {
      const newInc = roundToTwoDecimals(
        exTaxPrice + (exTaxPrice * taxRate) / 100,
      );
      onChange({ exTaxPrice, incTaxPrice: newInc });
      prevTaxRateRef.current = taxRate;
    }
  }, [taxRate, exTaxPrice, onChange]);

  const handleExTaxChange = (newEx: number) => {
    const newInc = roundToTwoDecimals(newEx + (newEx * taxRate) / 100);
    onChange({ ...value, exTaxPrice: newEx, incTaxPrice: newInc });
  };

  const handleIncTaxChange = (newInc: number) => {
    const newEx = roundToTwoDecimals(newInc / (1 + taxRate / 100));
    onChange({ ...value, incTaxPrice: newInc, exTaxPrice: newEx });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-sm font-medium text-gray-950">
        {customLabel ?? t("components.membersPriceBlock.title")}
      </div>
      <div className="flex flex-row gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Input
            value={exTaxPrice.toString()}
            onChange={(e) => {
              const val = parseFloat(e.target.value || "0");
              handleExTaxChange(val);
            }}
            type="number"
            label={t("components.membersPriceBlock.exTaxPrice.label")}
            isRequired
          />
          {exTaxError && <ErrorMessage text={exTaxError} />}
        </div>
        <Link04Icon className="mt-9 size-5" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Input
            value={incTaxPrice.toString()}
            onChange={(e) => {
              const val = parseFloat(e.target.value || "0");
              handleIncTaxChange(val);
            }}
            type="number"
            label={t("components.membersPriceBlock.incTaxPrice.label")}
            isRequired
          />
          {incTaxError && <ErrorMessage text={incTaxError} />}
        </div>
      </div>
    </div>
  );
};
